import { useLocalSearchParams } from 'expo-router';
import { useForm } from 'react-hook-form';
import { StyleSheet, View } from 'react-native';

import { useUpdateNonProfitOrgWiki } from '@/api/hooks/nonProfitOrgHooks';
import { NonProfitOrg } from '@/api/models/nonProfitOrgModels';
import BrandedButton from '@/components/buttons/BrandedButton';
import FormWrapper from '@/components/forms/FormWrapper';
import TextInputField from '@/components/inputs/TextInputField';
import OnboardingSection from '@/components/npo/onboarding/OnboardingSection';
import { useEndFlowModal } from '@/contexts/endFlowModalContext';
import { i18n } from '@/utils/i18n';
import { safeJsonParse } from '@/utils/jsonParse';
import { useWikiChangesNpoModal } from '@/contexts/wikiChangesNpoModalContext';

const NpoRevenueWiki = () => {
  const { npoJson } = useLocalSearchParams<{ npoJson: string }>();
  const { mutateAsync: nonProfitUpdateWiki, isPending: nonProfitIsPending } = useUpdateNonProfitOrgWiki();

  const { showError } = useEndFlowModal();
  const { setVisible, setNpoId } = useWikiChangesNpoModal();

  const nonProfitOrg = safeJsonParse<NonProfitOrg>(npoJson ?? '');

  const onSubmit = async (data: { revenue: number }) => {
    try {
      nonProfitUpdateWiki(
        {
          npoId: nonProfitOrg?.id ?? '',
          revenue: Number(data.revenue)
        },
        {
          onSuccess: () => {
            setNpoId(nonProfitOrg?.id ?? '');
            setVisible(true);
          },
          onError: (e) => {
            showError({ message: e.message });
            console.error('Error', e);
          }
        }
      );
    } catch (e) {
      console.error(e);
    }
  };

  const {
    control,
    handleSubmit,
    formState: { isValid, submitCount }
  } = useForm<{ revenue: number }>({
    defaultValues: { revenue: nonProfitOrg?.revenue },
    mode: 'onSubmit'
  });

  return (
    <FormWrapper scrollEnable={false}>
      <View style={styles.container}>
        <OnboardingSection
          label={i18n.t('sign_in.nonProfit.revenue.total_revenue')}
          infoButtonLabel={i18n.t('sign_in.nonProfit.revenue.total_revenue')}>
          <TextInputField
            type="numeric"
            currency
            control={control}
            name="revenue"
            placeholder={i18n.t('sign_in.nonProfit.enter_dollar_amount')}
            returnKeyType="done"
          />
        </OnboardingSection>

        <BrandedButton
          style={styles.doneButton}
          loading={nonProfitIsPending}
          disabled={!isValid && submitCount > 0}
          onPress={handleSubmit(onSubmit)}>
          {i18n.t('mix.done')}
        </BrandedButton>
      </View>
    </FormWrapper>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'column',
    justifyContent: 'space-between',
    gap: 25
  },

  doneButton: {
    width: '100%'
  }
});

export default NpoRevenueWiki;
